import React, { useEffect, useId, useRef, useState } from "react";
import { submitClassChangeRequest } from "../../service.js";
import { toast } from "react-toastify";
import Toast from "../../components/Toast";
import { Overlay, Modal, Header, Title, CloseBtn, Form, Label, Textarea, SubmitBtn } from "./styles.js";


// Class Change Request Modal Component
export default function ClassChangeRequestModal({ onClose, onSubmit }) {

    const [formData, setFormData] = useState({
        requestedChanges: ""
    });
    const [submitting, setSubmitting] = useState(false);
    // The ref blocks a second click that lands before the re-render disables Submit.
    const submittingRef = useRef(false);
    const titleId = useId();
    // Read on the first render, before autoFocus moves focus into the form.
    const [opener] = useState(() => document.activeElement);
    const mountedRef = useRef(false);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            // Hand focus back to the button that opened the form, if it is still there.
            if (opener && opener !== document.body && opener.isConnected) opener.focus();
        };
    }, [opener]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (submittingRef.current) return;

        const limits = {
            requestedChanges: 2000
        };

        const fieldLabels = {
            requestedChanges: "requested changes"
        };

        for (const [key, value] of Object.entries(formData)) {
            if (!value || !value.trim()) {
                toast(<Toast message={`Failed to submit request as ${fieldLabels[key]} is empty. Please try again`} />);
                return;
            }
            if (value.length > limits[key]) {
                toast(<Toast message={`Failed to submit request as character limit for ${fieldLabels[key]} has exceeded. Please try again.`} />);
                return;
            }
        }

        submittingRef.current = true;
        setSubmitting(true);
        try {
            const response = await submitClassChangeRequest(formData);
            // undefined: the session expired and service.js has already sent the
            // teacher to sign in (or shown the LMS relaunch notice). Nothing was sent.
            if (!response || response.errorMessage) {
                // modifyData already shows the error toast, so we just stop here
                return;
            }
            toast(<Toast message={'Request submitted successfully!'} />);
            // Closed with ✕ while the request was in flight: the parent may have
            // opened a new form since, which this late reply must not close.
            if (!mountedRef.current) return;
            if (onSubmit) onSubmit();
            if (onClose) onClose();
        } catch (error) {
            toast(<Toast message={'Failed to send request. Please try again.'} />);
        } finally {
            submittingRef.current = false;
            setSubmitting(false);
        }
    };

    return (
        <Overlay>
            {/* Its own aria-modal dialog: when opened over another aria-modal
                popup (Class Insights), screen readers would otherwise hide it. */}
            <Modal role="dialog" aria-modal="true" aria-labelledby={titleId}>
                <Header>
                    <Title id={titleId}>Class change request</Title>
                    <CloseBtn type="button" aria-label="Close" onClick={onClose}>✕</CloseBtn>
                </Header>

                <Form onSubmit={handleSubmit}>

                    <Label>
                        List of requested changes
                        <Textarea
                            name="requestedChanges"
                            placeholder="Inc. full student names AND class codes of old and new classes"
                            rows={4}
                            value={formData.requestedChanges}
                            onChange={handleChange}
                            autoFocus
                        />
                    </Label>

                    <SubmitBtn type="submit" disabled={submitting}>
                        Submit
                    </SubmitBtn>

                </Form>
            </Modal>
        </Overlay>
    );
}